import { InfoIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { editAboutMeArticleMetricProfile, getProfile } from "@/api/profile";
import { logout } from "@/auth";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@radix-ui/react-tooltip";
import toast from "react-hot-toast";

import { ProfileType } from "@/types/profile";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const UpdateUserInfo = () => {
  const [professorInfo, setProfessorInfo] = useState<Partial<ProfileType>>({
    scopusID: "",
    webOfScienceID: "",
    googleScholarID: ""
  });

  const [disableScopus, setDisableScopus] = useState(false);
  const [disableISI, setDisableISI] = useState(false);
  const [disableScholar, setDisableScholar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const fetchProfessorInfo = async () => {
      try {
        setLoading(true);
        const profileData = await getProfile();
        if (profileData) {
          setProfessorInfo({
            id: profileData.id,
            scopusID: profileData.scopusID ?? "",
            webOfScienceID: profileData.webOfScienceID ?? "",
            googleScholarID: profileData.googleScholarID ?? ""
          });
          // setDisableScopus(profileData.scopusID === null);
          // setDisableISI(profileData.webOfScienceID === null);
          // setDisableScholar(profileData.googleScholarID === null);
          setIsInitialized(true);
        }
      } catch (error) {
        console.error("Error fetching professor info:", error);
        toast.error("خطا در دریافت اطلاعات استاد");
      } finally {
        setLoading(false);
      }
    };

    fetchProfessorInfo();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!professorInfo.id) {
      toast.error("شناسه کاربر نامعتبر است");
      return;
    }

    try {
      setLoading(true);
      await editAboutMeArticleMetricProfile({
        id: professorInfo.id,
        scopus: disableScopus
          ? undefined
          : professorInfo.scopusID?.trim() || undefined,
        isi: disableISI
          ? undefined
          : professorInfo.webOfScienceID?.trim() || undefined,
        googleScholar: disableScholar
          ? undefined
          : professorInfo.googleScholarID?.trim() || undefined,
        // مقادیر پیش‌فرض
        nickName: [],
        moreEmails: [],
        affilations: [],
      });
      window.location.href = "/dashboard/";
    } catch {
      toast.error("خطا در به‌روزرسانی اطلاعات");
    } finally {
      window.location.href = "/dashboard/";
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfessorInfo((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="h-full w-full">
      <form onSubmit={handleSubmit}>
        <div className="space-y-5">
          {/* SCOPUS */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label
                className="flex items-center gap-2 text-sm text-slate-800"
                htmlFor="scopusID"
              >
                شناسه Scopus
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <InfoIcon size={16} className="text-slate-600" />
                    </TooltipTrigger>
                    <TooltipContent className="rounded bg-gray-50 p-2 text-xs">
                      پرکردن شناسه اساتید در پایگاه های مختلف برای تمامی اساتید
                      الزامی است
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </label>

              <label className="flex items-center gap-1 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={disableScopus}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setDisableScopus(isChecked);
                    setProfessorInfo((prev) => ({
                      ...prev,
                      scopusID: isChecked ? "" : prev.scopusID || ""
                    }));
                  }}
                />
                ندارم
              </label>
            </div>
            <Input
              type="text"
              id="scopusID"
              name="scopusID"
              value={professorInfo.scopusID || ""}
              onChange={handleInputChange}
              disabled={disableScopus}
              required={!disableScopus}
            />
          </div>

          {/* ISI */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label
                className="text-sm text-slate-800"
                htmlFor="webOfScienceID"
              >
                شناسه WebOfScience (ISI)
              </label>
              <label className="flex items-center gap-1 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={disableISI}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setDisableISI(isChecked);
                    setProfessorInfo((prev) => ({
                      ...prev,
                      webOfScienceID: isChecked ? "" : prev.webOfScienceID || ""
                    }));
                  }}
                />
                ندارم
              </label>
            </div>
            <Input
              type="text"
              id="webOfScienceID"
              name="webOfScienceID"
              value={professorInfo.webOfScienceID || ""}
              onChange={handleInputChange}
              disabled={disableISI}
              required={!disableISI}
            />
          </div>

          {/* Scholar */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label
                className="text-sm text-slate-800"
                htmlFor="googleScholarID"
              >
                شناسه Scholar
              </label>
              <label className="flex items-center gap-1 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={disableScholar}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setDisableScholar(isChecked);
                    setProfessorInfo((prev) => ({
                      ...prev,
                      googleScholarID: isChecked
                        ? ""
                        : prev.googleScholarID || ""
                    }));
                  }}
                />
                ندارم
              </label>
            </div>
            <Input
              type="text"
              id="googleScholarID"
              name="googleScholarID"
              value={professorInfo.googleScholarID || ""}
              onChange={handleInputChange}
              disabled={disableScholar}
              required={!disableScholar}
            />
          </div>

          <Button
            className="!mt-10 w-full"
            type="submit"
            disabled={loading || !isInitialized}
          >
            {loading ? "در حال ارسال..." : "ثبت و ادامه"}
          </Button>

          <Button
            variant="outline"
            className="!mt-3 w-full"
            onClick={() => logout()}
            disabled={loading}
            type="button"
          >
            انصراف
          </Button>
        </div>
      </form>
    </div>
  );
};

export default UpdateUserInfo;
